import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnInit, SimpleChanges, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DateTimePickerDirective } from '../../directives/date-time-picker.directive';
import { formatClockTime } from '../../utils/format';
import { AuthService } from '../../services/auth.service';
import { DialogService } from '../../services/dialog.service';
import { NotificationService } from '../../services/notification.service';
import { SalaService } from '../../services/sala.service';
import {
  AVAILABILITY_REASONS,
  AffectedReservation,
  AvailabilityBlock,
  AvailabilityBlockPayload,
  AvailabilityBlockReason,
  AvailabilityBlockService,
} from '../../services/availability-block.service';

const MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

interface BlockForm {
  /** 'business' = todo el negocio; si no, el id del espacio. */
  target: 'business' | number;
  start_date: string;
  end_date: string;
  all_day: boolean;
  start_time: string;
  end_time: string;
  reason: AvailabilityBlockReason;
  note: string;
}

/**
 * Días/horarios no disponibles (asuetos, mantenimiento, emergencias...).
 *  - Sin spaceId (dashboard): gestiona los bloqueos de todo el negocio y de
 *    cualquier espacio.
 *  - Con spaceId (detalle del espacio): muestra lo que le aplica (los suyos
 *    y los del negocio completo) y crea por defecto para ese espacio.
 * Los espacios siguen visibles; solo esas fechas dejan de aceptar reservas.
 */
@Component({
  selector: 'app-availability-blocks',
  standalone: true,
  imports: [CommonModule, FormsModule, DateTimePickerDirective],
  template: `
    <section class="ab" [class.ab-embedded]="!!spaceId">
      <header class="ab-head">
        <div class="ab-head-copy">
          <h2><i class="bi bi-calendar-x" aria-hidden="true"></i> Días no disponibles</h2>
          <p>{{ spaceId ? 'Fechas u horarios en que este espacio no recibe reservas' : 'Fechas u horarios en que tus espacios no reciben reservas' }} (asueto, mantenimiento, emergencias…). {{ spaceId ? 'Sigue visible' : 'Siguen visibles' }}, pero esas fechas no se pueden reservar.</p>
        </div>
        <button type="button" class="btn-primary ab-add" (click)="openForm()" [disabled]="!canEdit"><i class="bi bi-plus-lg" aria-hidden="true"></i> Bloquear fechas</button>
      </header>

      <div class="ab-alert" *ngIf="affected().length" role="status">
        <i class="bi bi-exclamation-triangle-fill" aria-hidden="true"></i>
        <div>
          <strong>{{ affected().length === 1 ? 'Hay 1 reserva activa' : 'Hay ' + affected().length + ' reservas activas' }} en esas fechas.</strong>
          <span>No se cancelan solas: contacta a {{ affected().length === 1 ? 'tu cliente' : 'tus clientes' }} para reprogramar o cancelar desde Reservas.</span>
          <ul>
            <li *ngFor="let r of affected().slice(0, 5)">#{{ r.reference }} · {{ formatDate(r.date) }} {{ formatClockTime(r.start_time) }}–{{ formatClockTime(r.end_time) }}<ng-container *ngIf="!spaceId && r.space_title"> · {{ r.space_title }}</ng-container><ng-container *ngIf="r.customer_name"> · {{ r.customer_name }}</ng-container></li>
            <li *ngIf="affected().length > 5">y {{ affected().length - 5 }} más…</li>
          </ul>
        </div>
        <button type="button" class="ab-icon-btn" (click)="affected.set([])" aria-label="Cerrar aviso"><i class="bi bi-x-lg"></i></button>
      </div>

      <p class="ab-state" *ngIf="loading()"><i class="bi bi-arrow-repeat ab-spin" aria-hidden="true"></i> Cargando…</p>
      <p class="ab-state ab-error" *ngIf="!loading() && loadError()">{{ loadError() }} <button type="button" class="ab-link" (click)="load()">Reintentar</button></p>

      <div class="ab-empty" *ngIf="!loading() && !loadError() && !blocks().length">
        <i class="bi bi-calendar-check" aria-hidden="true"></i>
        <span>No hay fechas bloqueadas. {{ spaceId ? 'El espacio recibe' : 'Tus espacios reciben' }} reservas según su horario.</span>
      </div>

      <ul class="ab-list" *ngIf="!loading() && blocks().length">
        <li *ngFor="let block of blocks(); trackBy: trackBlock" class="ab-item">
          <div class="ab-date" aria-hidden="true">
            <strong>{{ dayOf(block.start_date) }}</strong><span>{{ monthOf(block.start_date) }}</span>
          </div>
          <div class="ab-body">
            <strong class="ab-range">{{ rangeLabel(block) }}</strong>
            <span class="ab-time"><i class="bi" [ngClass]="block.all_day ? 'bi-sun' : 'bi-clock'" aria-hidden="true"></i> {{ block.all_day ? 'Todo el día' : formatClockTime(block.start_time) + ' – ' + formatClockTime(block.end_time) }}</span>
            <div class="ab-chips">
              <span class="ab-chip ab-reason"><i class="bi" [ngClass]="reasonIcon(block.reason)" aria-hidden="true"></i> {{ block.reason_label }}</span>
              <span class="ab-chip" [class.ab-chip-business]="block.scope === 'business'">
                <i class="bi" [ngClass]="block.scope === 'business' ? 'bi-buildings' : 'bi-door-open'" aria-hidden="true"></i>
                {{ block.scope === 'business' ? 'Todo el negocio' : (spaceId ? 'Solo este espacio' : (block.space_title || 'Espacio')) }}
              </span>
            </div>
            <small class="ab-note" *ngIf="block.note"><i class="bi bi-sticky" aria-hidden="true"></i> {{ block.note }}</small>
          </div>
          <div class="ab-actions" *ngIf="canEdit">
            <button type="button" class="ab-icon-btn" (click)="openForm(block)" [attr.aria-label]="'Editar bloqueo del ' + rangeLabel(block)" title="Editar"><i class="bi bi-pencil-square"></i></button>
            <button type="button" class="ab-icon-btn ab-danger" (click)="remove(block)" [disabled]="removingId() === block.block_id" [attr.aria-label]="'Quitar bloqueo del ' + rangeLabel(block)" title="Quitar bloqueo"><i class="bi" [ngClass]="removingId() === block.block_id ? 'bi-arrow-repeat ab-spin' : 'bi-trash3'"></i></button>
          </div>
        </li>
      </ul>
    </section>

    <!-- Formulario -->
    <div class="ab-overlay" *ngIf="form() as f" (click)="closeForm()">
      <div class="ab-modal" role="dialog" aria-modal="true" aria-labelledby="ab-form-title" (click)="$event.stopPropagation()" (keydown.escape)="onEscape($event)">
        <header class="ab-modal-head">
          <h3 id="ab-form-title"><i class="bi bi-calendar-x" aria-hidden="true"></i> {{ editing() ? 'Editar fechas no disponibles' : 'Bloquear fechas' }}</h3>
          <button type="button" class="ab-icon-btn" (click)="closeForm()" aria-label="Cerrar"><i class="bi bi-x-lg"></i></button>
        </header>

        <div class="ab-modal-body">
          <fieldset class="ab-field">
            <legend>¿A qué aplica?</legend>
            <div class="ab-segment" *ngIf="spaceId; else businessTarget">
              <button type="button" [class.active]="f.target !== 'business'" (click)="patch({ target: spaceId! })"><i class="bi bi-door-open" aria-hidden="true"></i> Solo este espacio</button>
              <button type="button" [class.active]="f.target === 'business'" (click)="patch({ target: 'business' })"><i class="bi bi-buildings" aria-hidden="true"></i> Todo el negocio</button>
            </div>
            <ng-template #businessTarget>
              <!-- Select nativo: el panel lo convierte en el selector del sistema (shared/select-enhancer). -->
              <select class="form-control" [ngModel]="f.target === 'business' ? 'business' : '' + f.target" (ngModelChange)="patch({ target: $event === 'business' ? 'business' : +$event })" aria-label="Aplica a">
                <option value="business">Todo el negocio (todos los espacios)</option>
                <option *ngFor="let s of spaces()" [value]="'' + s.space_id">{{ s.title }}</option>
              </select>
            </ng-template>
            <small class="ab-hint" *ngIf="f.target === 'business'">Todos tus espacios seguirán visibles, pero no podrán reservarse en esas fechas.</small>
          </fieldset>

          <fieldset class="ab-field">
            <legend>Fechas</legend>
            <div class="ab-grid-2">
              <label>Desde<input type="text" class="ab-input" appDatePicker="date" [minDate]="today()" [blockedDates]="takenDates()" [ngModel]="f.start_date" (ngModelChange)="onStartDate($event)" placeholder="Seleccionar fecha" autocomplete="off"></label>
              <label>Hasta<input type="text" class="ab-input" appDatePicker="date" [minDate]="f.start_date || today()" [maxDate]="endMaxDate()" [blockedDates]="takenDates()" [ngModel]="f.end_date" (ngModelChange)="patch({ end_date: $event })" placeholder="Seleccionar fecha" autocomplete="off"></label>
            </div>
            <small class="ab-hint" *ngIf="!rangeConflict()">{{ daysLabel(f) }}<ng-container *ngIf="takenDates().size"> · Los días ya bloqueados aparecen deshabilitados.</ng-container></small>
            <small class="ab-conflict" *ngIf="rangeConflict() as conflict" role="alert"><i class="bi bi-exclamation-circle" aria-hidden="true"></i> {{ conflict }}</small>
          </fieldset>

          <fieldset class="ab-field">
            <legend>Horario</legend>
            <label class="ab-switch">
              <input type="checkbox" [ngModel]="f.all_day" (ngModelChange)="patch({ all_day: $event })">
              <span class="ab-switch-ui" aria-hidden="true"></span>
              Todo el día
            </label>
            <div class="ab-grid-2" *ngIf="!f.all_day">
              <label>De<input type="text" class="ab-input" appDatePicker="time" [ngModel]="f.start_time" (ngModelChange)="patch({ start_time: $event })" placeholder="Hora de inicio" autocomplete="off"></label>
              <label>A<input type="text" class="ab-input" appDatePicker="time" [afterTime]="f.start_time" [ngModel]="f.end_time" (ngModelChange)="patch({ end_time: $event })" placeholder="Hora de fin" autocomplete="off"></label>
            </div>
            <small class="ab-hint" *ngIf="!f.all_day">Se bloquea esa franja en cada día del rango; el resto del día sigue disponible.</small>
          </fieldset>

          <fieldset class="ab-field">
            <legend>Motivo <small>(lo ven tus clientes)</small></legend>
            <div class="ab-reasons" role="radiogroup" aria-label="Motivo">
              <button type="button" *ngFor="let r of reasons" role="radio" [attr.aria-checked]="f.reason === r.value" [class.active]="f.reason === r.value" (click)="patch({ reason: r.value })"><i class="bi" [ngClass]="r.icon" aria-hidden="true"></i> {{ r.label }}</button>
            </div>
          </fieldset>

          <label class="ab-field ab-note-field">
            <span class="ab-legend">Nota interna <small>(opcional, solo tu equipo)</small><small class="ab-count">{{ f.note.length }}/200</small></span>
            <textarea class="ab-input ab-textarea" rows="2" maxlength="200" [ngModel]="f.note" (ngModelChange)="patch({ note: $event })" placeholder="Ej. Pintura de paredes; llamar a clientes del martes."></textarea>
          </label>

          <p class="ab-form-error" *ngIf="formError()" role="alert"><i class="bi bi-exclamation-circle" aria-hidden="true"></i> {{ formError() }}</p>
        </div>

        <footer class="ab-modal-foot">
          <button type="button" class="btn-secondary" (click)="closeForm()">Cancelar</button>
          <button type="button" class="btn-primary" (click)="save()" [disabled]="saving() || !!rangeConflict()"><i class="bi" [ngClass]="saving() ? 'bi-arrow-repeat ab-spin' : 'bi-check2'" aria-hidden="true"></i> {{ saving() ? 'Guardando…' : (editing() ? 'Guardar cambios' : 'Bloquear fechas') }}</button>
        </footer>
      </div>
    </div>
  `,
  styles: [`
    :host { display: block; }
    .ab { padding: 1.35rem; border: 1px solid var(--ts-border); border-radius: 16px; background: var(--ts-surface); color: var(--ts-text); }
    .ab-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem; margin-bottom: 1rem; }
    .ab-head h2 { display: flex; align-items: center; gap: .5rem; margin: 0 0 .3rem; font-size: 1.08rem; color: var(--ts-text); }
    .ab-head h2 .bi { color: var(--ts-danger-text); }
    .ab-head p { margin: 0; max-width: 62ch; color: var(--ts-text-muted); font-size: .84rem; line-height: 1.45; }
    .ab-add { flex: none; display: inline-flex; align-items: center; gap: .4rem; white-space: nowrap; }
    .ab-state { margin: .5rem 0; color: var(--ts-text-muted); font-size: .85rem; }
    .ab-error { color: var(--ts-danger-text); }
    .ab-link { padding: 0; border: 0; background: none; color: var(--ts-primary); font: inherit; font-weight: 700; text-decoration: underline; cursor: pointer; }
    .ab-empty { display: flex; align-items: center; gap: .6rem; padding: .9rem 1rem; border: 1px dashed var(--ts-border-strong); border-radius: 12px; color: var(--ts-text-muted); font-size: .85rem; }
    .ab-empty .bi { color: var(--ts-success-text); font-size: 1.1rem; }
    .ab-list { list-style: none; margin: 0; padding: 0; display: grid; gap: .6rem; }
    .ab-item { display: grid; grid-template-columns: auto minmax(0, 1fr) auto; align-items: center; gap: .9rem; padding: .75rem .85rem; border: 1px solid var(--ts-border); border-radius: 12px; background: var(--ts-surface-2); }
    .ab-date { width: 48px; display: grid; justify-items: center; padding: .35rem 0; border-radius: 10px; background: var(--ts-danger-soft); color: var(--ts-danger-text); line-height: 1.05; }
    .ab-date strong { font-size: 1.15rem; }
    .ab-date span { font-size: .68rem; font-weight: 700; text-transform: uppercase; }
    .ab-body { min-width: 0; display: grid; gap: .25rem; }
    .ab-range { font-size: .9rem; color: var(--ts-text); }
    .ab-time { color: var(--ts-text-muted); font-size: .8rem; }
    .ab-chips { display: flex; flex-wrap: wrap; gap: .35rem; }
    .ab-chip { display: inline-flex; align-items: center; gap: .3rem; max-width: 100%; padding: .15rem .55rem; border: 1px solid var(--ts-border); border-radius: 999px; color: var(--ts-text-muted); font-size: .72rem; font-weight: 650; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .ab-reason { color: var(--ts-warning-text); background: var(--ts-warning-soft); border-color: transparent; }
    .ab-chip-business { color: var(--ts-primary); border-color: currentColor; }
    .ab-note { color: var(--ts-text-muted); font-size: .76rem; overflow-wrap: anywhere; }
    .ab-actions { display: flex; gap: .3rem; }
    .ab-icon-btn { width: 34px; height: 34px; min-height: 0; display: inline-grid; place-items: center; padding: 0; border: 1px solid var(--ts-border); border-radius: 9px; color: var(--ts-text-muted); background: var(--ts-surface); cursor: pointer; }
    .ab-icon-btn:hover { color: var(--ts-primary); border-color: var(--ts-primary); }
    .ab-icon-btn.ab-danger:hover { color: var(--ts-danger-text); border-color: var(--ts-danger-text); }
    .ab-icon-btn:focus-visible, .ab-segment button:focus-visible, .ab-reasons button:focus-visible { outline: 2px solid var(--ts-primary); outline-offset: 2px; }
    .ab-alert { display: grid; grid-template-columns: auto minmax(0, 1fr) auto; gap: .7rem; align-items: start; margin-bottom: 1rem; padding: .8rem .9rem; border-radius: 12px; color: var(--ts-warning-text); background: var(--ts-warning-soft); font-size: .82rem; line-height: 1.45; }
    .ab-alert > .bi { font-size: 1.05rem; margin-top: .1rem; }
    .ab-alert strong, .ab-alert span { display: block; }
    .ab-alert ul { margin: .35rem 0 0; padding-left: 1.1rem; }
    .ab-alert .ab-icon-btn { width: 28px; height: 28px; background: transparent; border-color: transparent; color: inherit; }
    .ab-spin { display: inline-block; animation: abSpin 1s linear infinite; }
    @keyframes abSpin { to { transform: rotate(360deg); } }

    .ab-overlay { position: fixed; inset: 0; z-index: 2600; display: grid; place-items: center; padding: 16px; background: rgba(3, 12, 27, .55); backdrop-filter: blur(3px); }
    .ab-modal { width: min(560px, 100%); max-height: calc(100vh - 32px); display: flex; flex-direction: column; overflow: hidden; border: 1px solid var(--ts-border-strong); border-radius: 16px; background: var(--ts-surface); color: var(--ts-text); box-shadow: 0 24px 64px rgba(2, 8, 23, .35); }
    .ab-modal-head, .ab-modal-foot { display: flex; align-items: center; justify-content: space-between; gap: .75rem; padding: .9rem 1.1rem; border-bottom: 1px solid var(--ts-border); }
    .ab-modal-foot { justify-content: flex-end; border-top: 1px solid var(--ts-border); border-bottom: 0; }
    .ab-modal-foot button { display: inline-flex; align-items: center; gap: .4rem; }
    .ab-modal-head h3 { display: flex; align-items: center; gap: .5rem; margin: 0; font-size: 1rem; }
    .ab-modal-head h3 .bi { color: var(--ts-danger-text); }
    .ab-modal-body { display: grid; gap: 1rem; padding: 1rem 1.1rem; overflow-y: auto; }
    .ab-field { display: grid; gap: .45rem; min-width: 0; margin: 0; padding: 0; border: 0; }
    .ab-field legend, .ab-legend { display: flex; align-items: baseline; gap: .35rem; margin-bottom: .1rem; padding: 0; color: var(--ts-text); font-size: .8rem; font-weight: 750; }
    .ab-field legend small, .ab-legend small { color: var(--ts-text-muted); font-weight: 500; }
    .ab-count { margin-left: auto; }
    .ab-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: .6rem; }
    .ab-grid-2 label { display: grid; gap: .25rem; color: var(--ts-text-muted); font-size: .76rem; font-weight: 650; }
    .ab-input { width: 100%; box-sizing: border-box; min-height: 40px; padding: .5rem .65rem; border: 1px solid var(--ts-border-strong); border-radius: 10px; color: var(--ts-text); background: var(--ts-surface); font: inherit; font-size: .88rem; }
    .ab-textarea { width: 100%; box-sizing: border-box; resize: vertical; min-height: 60px; }
    .ab-input:focus { outline: none; border-color: var(--ts-primary); box-shadow: 0 0 0 3px rgba(47, 124, 255, .18); }
    .ab-hint { color: var(--ts-text-muted); font-size: .75rem; }
    .ab-conflict { display: flex; gap: .35rem; color: var(--ts-danger-text); font-size: .76rem; font-weight: 650; line-height: 1.4; }
    .ab-segment { display: grid; grid-template-columns: 1fr 1fr; gap: .4rem; }
    .ab-segment button, .ab-reasons button { display: inline-flex; align-items: center; justify-content: center; gap: .35rem; min-height: 38px; padding: .4rem .6rem; border: 1px solid var(--ts-border-strong); border-radius: 10px; color: var(--ts-text-muted); background: var(--ts-surface); font: inherit; font-size: .8rem; font-weight: 650; cursor: pointer; }
    .ab-segment button.active, .ab-reasons button.active { color: var(--ts-primary); border-color: var(--ts-primary); background: rgba(47, 124, 255, .1); }
    .ab-reasons { display: flex; flex-wrap: wrap; gap: .4rem; }
    .ab-switch { display: inline-flex; align-items: center; gap: .55rem; width: fit-content; color: var(--ts-text); font-size: .85rem; font-weight: 650; cursor: pointer; }
    .ab-switch input { position: absolute; opacity: 0; pointer-events: none; }
    .ab-switch-ui { position: relative; width: 38px; height: 22px; border-radius: 999px; background: var(--ts-border-strong); transition: background .2s ease; }
    .ab-switch-ui::after { content: ''; position: absolute; top: 3px; left: 3px; width: 16px; height: 16px; border-radius: 50%; background: #fff; transition: transform .2s ease; }
    .ab-switch input:checked + .ab-switch-ui { background: var(--ts-primary); }
    .ab-switch input:checked + .ab-switch-ui::after { transform: translateX(16px); }
    .ab-switch input:focus-visible + .ab-switch-ui { outline: 2px solid var(--ts-primary); outline-offset: 2px; }
    .ab-form-error { display: flex; gap: .4rem; margin: 0; padding: .6rem .75rem; border-radius: 10px; color: var(--ts-danger-text); background: var(--ts-danger-soft); font-size: .82rem; }

    @media (max-width: 640px) {
      .ab { padding: 1rem; }
      .ab-head { flex-direction: column; }
      .ab-add { width: 100%; justify-content: center; }
      .ab-item { grid-template-columns: auto minmax(0, 1fr); }
      .ab-actions { grid-column: 1 / -1; justify-content: flex-end; }
      .ab-grid-2, .ab-segment { grid-template-columns: 1fr; }
    }
    @media (prefers-reduced-motion: reduce) { .ab-spin { animation: none; } .ab-switch-ui, .ab-switch-ui::after { transition: none; } }
  `],
})
export class AvailabilityBlocksComponent implements OnInit, OnChanges {
  readonly formatClockTime = formatClockTime;
  /** Con espacio: muestra lo que le aplica y crea para él por defecto. */
  @Input() spaceId: number | null = null;
  /** Permiso para crear/editar/quitar (spaces.edit). */
  @Input() canEdit = true;

  private readonly service = inject(AvailabilityBlockService);
  private readonly auth = inject(AuthService);
  private readonly dialogs = inject(DialogService);
  private readonly notifications = inject(NotificationService);
  private readonly salas = inject(SalaService);

  readonly reasons = AVAILABILITY_REASONS;
  readonly blocks = signal<AvailabilityBlock[]>([]);
  readonly spaces = signal<Array<{ space_id: number; title: string }>>([]);
  readonly loading = signal(false);
  readonly loadError = signal('');
  readonly form = signal<BlockForm | null>(null);
  readonly editing = signal<AvailabilityBlock | null>(null);
  readonly saving = signal(false);
  readonly formError = signal('');
  readonly removingId = signal<number | null>(null);
  readonly affected = signal<AffectedReservation[]>([]);
  readonly today = signal(this.localToday());

  private readonly businessId = computed(() => this.auth.currentUser()?.id ?? null);
  /** Todos los bloqueos vigentes del negocio, para saber qué días ya están tomados. */
  private readonly allBlocks = signal<AvailabilityBlock[]>([]);

  /**
   * Días que este bloqueo NO puede usar porque ya están bloqueados para el
   * mismo alcance (un bloqueo del negocio completo cubre a todos los espacios)
   * y se cruzan en horario -- mismo criterio que el backend
   * (findOverlappingBlock). Fecha -> motivo, para el tooltip del calendario.
   */
  readonly takenDates = computed(() => {
    const f = this.form();
    const map = new Map<string, string>();
    if (!f) return map;
    const editingId = this.editing()?.block_id;
    for (const block of this.allBlocks()) {
      if (block.block_id === editingId) continue;
      if (f.target !== 'business' && block.space_id !== null && block.space_id !== f.target) continue;
      const crosses = f.all_day || block.all_day || ((block.start_time ?? '') < f.end_time && (block.end_time ?? '') > f.start_time);
      if (!crosses) continue;
      const scope = block.scope === 'business' ? 'todo el negocio' : (block.space_title || 'un espacio');
      const hours = block.all_day ? 'todo el día' : `${formatClockTime(block.start_time)}–${formatClockTime(block.end_time)}`;
      const label = `Ya bloqueado: ${block.reason_label} (${scope}, ${hours})`;
      const first = block.start_date < this.today() ? this.today() : block.start_date;
      for (let date = first; date <= block.end_date; date = this.addDays(date, 1)) {
        if (!map.has(date)) map.set(date, label);
      }
    }
    return map;
  });

  /** "Hasta" llega como máximo al día anterior al siguiente bloqueo: el rango no puede saltarlo. */
  readonly endMaxDate = computed(() => {
    const start = this.form()?.start_date;
    if (!start) return null;
    const next = [...this.takenDates().keys()].filter(date => date > start).sort()[0];
    return next ? this.addDays(next, -1) : null;
  });

  /** Aviso en vivo si el rango elegido ya incluye días bloqueados. */
  readonly rangeConflict = computed(() => {
    const f = this.form();
    if (!f?.start_date || !f.end_date || f.end_date < f.start_date) return '';
    for (let date = f.start_date; date <= f.end_date; date = this.addDays(date, 1)) {
      const taken = this.takenDates().get(date);
      if (taken) return `El ${this.formatDate(date)} ya está bloqueado (${taken.replace(/^Ya bloqueado: /, '')}). Elige otras fechas${f.all_day ? ' u horas' : ' u horas que no se crucen'}.`;
    }
    return '';
  });

  ngOnInit(): void {
    this.load();
    if (!this.spaceId) {
      this.salas.obtenerEspaciosParaSelector().subscribe({
        next: list => this.spaces.set(list.map(s => ({ space_id: s.space_id, title: s.title }))),
        error: () => this.spaces.set([]),
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['spaceId'] && !changes['spaceId'].firstChange) this.load();
  }

  load(): void {
    const businessId = this.businessId();
    if (!businessId) return;
    this.loading.set(true);
    this.loadError.set('');
    this.service.list(businessId, this.spaceId).subscribe({
      next: response => {
        this.blocks.set(response.blocks);
        if (response.today) this.today.set(response.today);
        this.loading.set(false);
      },
      error: (error: Error) => {
        this.loadError.set(error.message);
        this.loading.set(false);
      },
    });
  }

  openForm(block?: AvailabilityBlock): void {
    if (!this.canEdit) return;
    this.formError.set('');
    this.editing.set(block ?? null);
    const defaultTarget: BlockForm['target'] = this.spaceId ?? 'business';
    this.form.set(block ? {
      target: block.space_id ?? 'business',
      start_date: block.start_date < this.today() ? this.today() : block.start_date,
      end_date: block.end_date,
      all_day: block.all_day,
      start_time: block.start_time ?? '08:00',
      end_time: block.end_time ?? '12:00',
      reason: block.reason,
      note: block.note ?? '',
    } : {
      target: defaultTarget,
      start_date: this.today(),
      end_date: this.today(),
      all_day: true,
      start_time: '08:00',
      end_time: '12:00',
      reason: 'holiday',
      note: '',
    });
    this.loadAllBlocks(!block);
  }

  /** Bloqueos de todo el negocio; en un bloqueo nuevo, si hoy ya está tomado, deja las fechas vacías para elegir. */
  private loadAllBlocks(isNew: boolean): void {
    const businessId = this.businessId();
    if (!businessId) return;
    this.service.list(businessId).subscribe({
      next: response => {
        this.allBlocks.set(response.blocks);
        const f = this.form();
        if (isNew && f && !this.editing() && this.takenDates().has(f.start_date)) {
          this.form.set({ ...f, start_date: '', end_date: '' });
        }
      },
      error: () => this.allBlocks.set([]),
    });
  }

  /**
   * Escape cierra primero el calendario/selector de hora o el selector abierto
   * (ellos mismos lo manejan); solo si no hay ninguno, cierra el formulario.
   */
  onEscape(event: Event): void {
    // flatpickr cierra su calendario en el mismo keydown (antes de que llegue
    // aquí), así que no basta con ver si sigue abierto: si Escape viene de un
    // campo de fecha/hora, solo cierra ese calendario.
    const target = event.target as HTMLElement | null;
    if (target?.closest('.ab-grid-2') || document.querySelector('.flatpickr-calendar.open, .ts-select-panel')) return;
    this.closeForm();
  }

  closeForm(): void {
    if (this.saving()) return;
    this.form.set(null);
    this.editing.set(null);
  }

  patch(changes: Partial<BlockForm>): void {
    const current = this.form();
    if (current) this.form.set({ ...current, ...changes });
    this.formError.set('');
  }

  /** Al mover "desde", "hasta" nunca queda antes. */
  onStartDate(value: string): void {
    const current = this.form();
    if (!current) return;
    let end = !current.end_date || current.end_date < value ? value : current.end_date;
    const next = [...this.takenDates().keys()].filter(date => date > value).sort()[0];
    if (next && end >= next) end = value;
    this.patch({ start_date: value, end_date: end });
  }

  save(): void {
    const f = this.form();
    const businessId = this.businessId();
    if (!f || !businessId || this.saving()) return;
    const error = this.validate(f);
    if (error) { this.formError.set(error); return; }

    const payload: AvailabilityBlockPayload = {
      space_id: f.target === 'business' ? null : f.target,
      start_date: f.start_date,
      end_date: f.end_date,
      all_day: f.all_day,
      start_time: f.all_day ? null : f.start_time,
      end_time: f.all_day ? null : f.end_time,
      reason: f.reason,
      note: f.note.trim() || null,
    };
    const editing = this.editing();
    this.saving.set(true);
    const request = editing ? this.service.update(editing.block_id, payload) : this.service.create(businessId, payload);
    request.subscribe({
      next: response => {
        this.saving.set(false);
        this.form.set(null);
        this.editing.set(null);
        this.affected.set(response.affected_reservations ?? []);
        this.notifications.success(editing ? 'Fechas no disponibles actualizadas.' : 'Listo: esas fechas ya no aceptan reservas.');
        this.load();
      },
      error: (err: Error) => {
        this.saving.set(false);
        this.formError.set(err.message);
      },
    });
  }

  async remove(block: AvailabilityBlock): Promise<void> {
    const ok = await this.dialogs.confirm({
      tone: 'danger',
      title: 'Quitar bloqueo',
      message: `${this.rangeLabel(block)} (${block.all_day ? 'todo el día' : formatClockTime(block.start_time) + '–' + formatClockTime(block.end_time)}) volverá a estar disponible para reservas${block.scope === 'business' ? ' en todos tus espacios' : ''}.`,
      confirmText: 'Quitar bloqueo',
    });
    if (!ok) return;
    this.removingId.set(block.block_id);
    this.service.remove(block.block_id).subscribe({
      next: response => {
        this.removingId.set(null);
        this.blocks.update(list => list.filter(item => item.block_id !== block.block_id));
        this.notifications.success(response.msg);
      },
      error: (err: Error) => {
        this.removingId.set(null);
        this.notifications.error(err.message);
      },
    });
  }

  private validate(f: BlockForm): string | null {
    if (!f.start_date || !f.end_date) return 'Elige la fecha de inicio y de fin.';
    if (f.end_date < f.start_date) return 'La fecha "hasta" no puede ser anterior a "desde".';
    if (f.end_date < this.today()) return 'Esas fechas ya pasaron.';
    if (!f.all_day) {
      if (!f.start_time || !f.end_time) return 'Indica la hora de inicio y de fin.';
      if (f.end_time <= f.start_time) return 'La hora de fin debe ser posterior a la de inicio.';
    }
    if (f.note.length > 200) return 'La nota admite hasta 200 caracteres.';
    return null;
  }

  // ---- Presentación ----
  trackBlock = (_: number, block: AvailabilityBlock) => block.block_id;

  reasonIcon(reason: AvailabilityBlockReason): string {
    return this.reasons.find(r => r.value === reason)?.icon ?? 'bi-slash-circle';
  }

  dayOf(iso: string): number { return Number(iso.slice(8, 10)); }
  monthOf(iso: string): string { return MONTHS[Number(iso.slice(5, 7)) - 1] ?? ''; }

  formatDate(iso: string): string {
    return `${this.dayOf(iso)} ${this.monthOf(iso)} ${iso.slice(0, 4)}`;
  }

  rangeLabel(block: { start_date: string; end_date: string }): string {
    if (block.start_date === block.end_date) return this.formatDate(block.start_date);
    const sameYear = block.start_date.slice(0, 4) === block.end_date.slice(0, 4);
    const start = sameYear ? `${this.dayOf(block.start_date)} ${this.monthOf(block.start_date)}` : this.formatDate(block.start_date);
    return `${start} – ${this.formatDate(block.end_date)}`;
  }

  daysLabel(f: BlockForm): string {
    if (!f.start_date || !f.end_date || f.end_date < f.start_date) return '';
    const [ay, am, ad] = f.start_date.split('-').map(Number);
    const [by, bm, bd] = f.end_date.split('-').map(Number);
    const days = Math.round((Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / 86400000) + 1;
    return days === 1 ? '1 día' : `${days} días (ambas fechas incluidas)`;
  }

  private addDays(iso: string, days: number): string {
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
  }

  private localToday(): string {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  }
}
